import { Button, Col, Layout, Row, Typography } from "antd";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import ChevronBackIcon from "../../../assets/icons/chevron-back-white.svg";
import ChevronLeftIcon from "../../../assets/icons/chevron-left-collection.svg";
import ChevronRightIcon from "../../../assets/icons/chevron-right-collection.svg";
import OptionIcon from "../../../assets/icons/collection-option.svg";
import VideoIcon from "../../../assets/icons/jouer.svg";
import MediaComponent from "../../../components/MediaComponent";
import OptionsModalComponent from "../../../components/OptionsModalComponent/OptionsModalComponent";
import ReportModal from "../../../components/ReportModal";
import SpinnerComponent from "../../../components/SpinnerComponent/SpinnerComponent";
import { PAYMENT_TYPES, CREATOR } from "../../../helpers/constant";
import { IFanCollection, IOtherUserInfoWrapper } from "../../../helpers/types";
import { authService, userService } from "../../../services";
import { collectionService } from "../../../services/collection.service";
import "./index.less";
import { subscriptionsService } from "../../../services";
import { LoadingOutlined } from "@ant-design/icons";
import AnauthorizedAccessPopup from "../../../components/AnauthorizedAccessPopup/AnauthorizedAccessPopup";
import { APP_DOMAIN } from "../../../utls/constants";

const CollectionPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { collectionId, username } = useParams();
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [collectionData, setCollectionData] = useState<IFanCollection | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [displayPrivateImage, setDisplayPrivateImage] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState<boolean>(false);
  const [isOptionsModalOpen, setIsOptionsModalOpen] = useState<boolean>(false);
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(authService.getRole());
  const [creatorRole, setCreatorRole] = useState<string | null>(null);
  const [isCreatorRoleLoading, setIsCreatorRoleLoading] = useState<boolean>(false);
  const [roleCheckComplete, setRoleCheckComplete] = useState<boolean>(false);
  const [canShowPaymentButtons, setCanShowPaymentButtons] = useState<boolean>(false);
  const params: any = new URLSearchParams(window.location.search);
  const from: any = params.get("from");
  const collectionTab: any = params.get("ctc");
  const [isLoadingEligible, setIsLoadingEligible] = useState<boolean>(false);
  const [isAccessAnauthorized, setIsAccessAnauthorized] = useState<boolean>(false);

  useEffect(() => {
    if (collectionId) {
      setIsLoading(true);
      setRoleCheckComplete(false); // Reset role check state
      setCanShowPaymentButtons(false); // Reset payment buttons state
      collectionService
        .getFanCollectionDetail(collectionId)
        .then((data: IFanCollection) => {
          data.files = data.files.sort((a, b) => Number(b.isThumbnail) - Number(a.isThumbnail));
          setCollectionData(data);

          // Fetch creator's role information only if current user is a creator
          if (data.creatorId && currentUserRole === CREATOR) {
            setIsCreatorRoleLoading(true);
            userService
              .getProfile(data.creatorId)
              .then((creatorData) => {
                setCreatorRole(creatorData.type || null);
                setIsCreatorRoleLoading(false);
                setRoleCheckComplete(true);
              })
              .catch((error) => {
                console.error("Error fetching creator role:", error);
                setIsCreatorRoleLoading(false);
                setRoleCheckComplete(true);
              });
          } else {
            // If current user is not a creator, no need to check collection owner role
            setRoleCheckComplete(true);
          }

          setIsLoading(false);
        })
        .catch((error) => {
          if (error === "user-is-blocked") {
            setIsAccessAnauthorized(true);
          }
          setIsLoading(false);
          setRoleCheckComplete(true);
        });
    }
  }, [collectionId, currentUserRole]);

  // Control when payment buttons can be shown to prevent flash
  useEffect(() => {
    if (roleCheckComplete && collectionData) {
      const shouldShow = !(currentUserRole === CREATOR && creatorRole === CREATOR);
      setCanShowPaymentButtons(shouldShow);
    } else {
      setCanShowPaymentButtons(false);
    }
  }, [roleCheckComplete, collectionData, currentUserRole, creatorRole]);

  const [isEligibleForFreeSubscription, setIsEligibleForFreeSubscription] = useState<boolean>(false);

  useEffect(() => {
    //NOTE: I SEND COLLECTION TYPE TO SHOW THE 2 BUTTONS IF THE FAN WANT TO SUBSCRIBE AGAIN BY COMBINATION PAYMENT
    if (collectionData?.creatorId && collectionData?.isFreeSubscription) {
      setIsLoadingEligible(true);
      subscriptionsService
        .checkSubscriptionEligibility({ creatorId: collectionData?.creatorId, type: "collection" })
        .then((data) => {
          setIsLoadingEligible(false);
          setIsEligibleForFreeSubscription(data.isEligibleForFreeSubscription);
        })
        .catch((error) => {
          setIsLoadingEligible(false);
          setIsEligibleForFreeSubscription(false);
        });
    }
  }, [collectionData?.creatorId, collectionData?.isFreeSubscription]);

  useLayoutEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "auto",
    });
  }, [collectionId]);

  const handleNext = () => {
    setCurrentFileIndex((prevIndex) => {
      if (collectionData && prevIndex < collectionData.files.length - 1) {
        return prevIndex + 1;
      }
      return prevIndex;
    });
  };

  const handlePrev = () => {
    setCurrentFileIndex((prevIndex) => {
      if (prevIndex > 0) {
        return prevIndex - 1;
      }
      return prevIndex;
    });
  };

  const handlePayForCollection = () => {
    navigate(
      `/common/payment?creatorId=${collectionData?.creatorId}&type=${PAYMENT_TYPES.COLLECTION}&collectionId=${collectionData?._id}&redirectUrl=common/collection/${collectionData?._id}/${username}`
    );
  };

  const handleSubscribeToCreator = () => {
    navigate(
      `/common/subscribe/${collectionData?.creatorId}?isFromCollection=true&redirectUrl=/common/collection/${collectionData?._id}/${username}`
    );
  };

  const handlePaymentOrSubscription = () => {
    if (displayPrivateImage) {
      setDisplayPrivateImage(false);
    }
    if (collectionData?.isSubscribed) {
      handlePayForCollection();
    } else if (collectionData?.isFreeSubscription && isEligibleForFreeSubscription) {
      handlePayForCollection();
    } else {
      handleSubscribeToCreator();
    }
  };

  const handleNavigate = () => {
    if (from && from === "paymentPage") {
      sessionStorage.setItem("payment-page", "True");
    }
    if (collectionTab) {
      navigate(`/p/${collectionData?.username}?ctc=collections&search=true`, { replace: true });
    } else {
      navigate(`/p/${collectionData?.username}?ctc=collections`, { replace: true });
    }
  };

  const privateImageClick = useCallback(() => {
    setDisplayPrivateImage(true);
  }, []);

  const closeReportModal = useCallback(() => {
    setIsReportModalOpen(false);
  }, []);

  const closeOptionsModal = useCallback(() => {
    setIsOptionsModalOpen(false);
  }, []);

  const onReportClick = useCallback(() => {
    setIsOptionsModalOpen(false);
    setIsReportModalOpen(true);
  }, []);

  const onOptionsClick = useCallback(() => {
    setIsOptionsModalOpen(true);
  }, []);

  // Helper function to determine if payment buttons should be shown
  const shouldShowPaymentButtons = () => {
    return (
      canShowPaymentButtons &&
      !collectionData?.isPaymentVerified &&
      currentUserRole &&
      currentUserRole !== ""
    );
  };

  const handleCloseAnauthorizedAccessPopup = () => {
    navigate(`${APP_DOMAIN}/`);
    setIsAccessAnauthorized(false);
  };

  const currentFile = collectionData?.files[currentFileIndex];

  return (
    <Layout className="gradient-background-profile">
      <Layout.Content className="fan-collection-page-wrapper background-transparent">
        <Row className="collection-header">
          <div className="collection-back-button" onClick={handleNavigate}>
            <img src={ChevronBackIcon} width={13} alt="back" />
          </div>
          <div className="collection-option-button" onClick={onOptionsClick}>
            <img src={OptionIcon} width={24} alt="option" />
          </div>
        </Row>
        {isLoading ? (
          <Row className="w-full justify-content-center mt-20">
            <SpinnerComponent />
          </Row>
        ) : (
          <>
            <Row className="mt-20 justofy-content-center">
              <Typography.Text className="collection-title-text">{collectionData?.title}</Typography.Text>
            </Row>
            <Row className="mt-10">
              <Typography.Text className="collection-price-text">{`${t("for-1")} ${collectionData?.price ? collectionData.price.toFixed(2) : "0.00"} €`}</Typography.Text>
            </Row>
            <div className="img-container">
              <div
                className="image-current-wrapper"
                onClick={(e) => {
                  if (currentFile?.isPrivate && !collectionData?.isPaymentVerified) {
                    privateImageClick();
                    e.stopPropagation();
                  }
                }}
              >
                <MediaComponent
                  key={currentFile?.url}
                  width={340}
                  height={320}
                  borderRadius={11}
                  image={currentFile?.thumbnailUrl!}
                  isPrivate={currentFile?.isPrivate && !collectionData?.isPaymentVerified}
                  isVideo={currentFile?.type.includes("video")}
                  url={currentFile?.url}
                  shouldAddWatermark={true}
                  fromCollection={true}
                />
              </div>
            </div>

            <Row className="img-switch-container">
              {currentFileIndex > 0 ? (
                <img src={ChevronLeftIcon} width={26} alt="left" onClick={handlePrev} />
              ) : (
                <div style={{ width: "24px" }}></div>
              )}

              <div className="preview-container">
                {[currentFileIndex - 2, currentFileIndex - 1, currentFileIndex, currentFileIndex + 1, currentFileIndex + 2].map((index) => {
                  const file = collectionData?.files[index];
                  const isActive = index === currentFileIndex;

                  // Skip rendering if no file exists at the index
                  if (!file) return null;

                  return (
                    <div key={index} className={`preview-wrapper ${isActive ? "active" : ""}`}>
                      <img
                        src={file?.thumbnailUrl}
                        width={isActive ? 100 : 80}
                        alt={isActive ? "current" : "preview"}
                        className="preview-image"
                        onClick={() => {
                          if (index < currentFileIndex) handlePrev();
                          else if (index > currentFileIndex) handleNext();
                        }}
                      />
                      {file.type.includes("video") && <img src={VideoIcon} className="video-icon" alt="video-icon" />}
                    </div>
                  );
                })}
              </div>

              {currentFileIndex < collectionData?.files.length! - 1 ? (
                <img src={ChevronRightIcon} width={26} alt="right" onClick={handleNext} />
              ) : (
                <div style={{ width: "24px" }}></div>
              )}
            </Row>

            <Row className="mt-20 resume-container">
              <Typography.Text className="description-text">{collectionData?.description}</Typography.Text>
            </Row>
            <Row className="mt-20 collection-content-container">
              <Col className="collection-content-col">
                <Typography.Text className="collection-content-title">{t("collection")}</Typography.Text>
                <Typography.Text className="collection-content-text">{t("what-does-it-contain")}</Typography.Text>
              </Col>
              <Col className="collection-content-col">
                <Typography.Text className="collection-content-text">
                  {collectionData?.imagesCount} {collectionData?.imagesCount && collectionData.imagesCount > 1 ? t("photos") : t("photo")} &{" "}
                  {collectionData?.videoCount} {collectionData?.videoCount && collectionData.videoCount > 1 ? t("videos") : t("video")}
                </Typography.Text>
              </Col>
            </Row>
            {shouldShowPaymentButtons() && (
              <Row className="collection-btn-sticky-row">
                <Button disabled={isLoadingEligible} className="collection-sticky-btn" onClick={handlePaymentOrSubscription}>
                  {isLoadingEligible ? (
                    <div className="spinner-container">
                      <LoadingOutlined style={{ fontSize: 30, color: "#000000" }} spin />
                    </div>
                  ) : (
                    t("pay-to-see")
                  )}
                </Button>
              </Row>
            )}
          </>
        )}

        {displayPrivateImage && (
          <div className="private-image-overlay" onClick={() => setDisplayPrivateImage(false)}>
            <div className="private-content-wrapper">
              <img
                className="private-large-image"
                src={currentFile?.type.includes("video") ? currentFile.thumbnailUrl! : currentFile?.url!}
                alt="Avatar en grand"
              />
              <div className="private-text-container">
                {/* <Row>
                  <Typography.Text className="reserved-title">{t("reserved-for-subscribers")}</Typography.Text>
                </Row>
                <Row>
                  <Typography.Text className="reserved-description">{t("reserved-for-subscribers-description")}</Typography.Text>
                </Row> */}
                {/* Hide subscribe/payment button when current user is a creator and collection owner is also a creator */}
                {shouldShowPaymentButtons() && (
                  <Button onClick={handlePaymentOrSubscription} type="link" className="pay-to-see-btn">
                    {t("unlocking-collection")}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </Layout.Content>
      <OptionsModalComponent
        reportBtnLabel={t("report-collection")}
        isOpen={isOptionsModalOpen}
        onClose={closeOptionsModal}
        onReportClick={onReportClick}
      />
      <ReportModal isOpen={isReportModalOpen} target="collection" targetId={collectionData?._id!} onClose={closeReportModal} />
      <AnauthorizedAccessPopup isOpen={isAccessAnauthorized} onClose={handleCloseAnauthorizedAccessPopup} />
    </Layout>
  );
};

export default CollectionPage;
